// `initExtensionCriteriaNavigation` / `calculateExtensionCriteriaUsedOptions`
// (shortlist-extension-alerts.component.ng1.ts:572) — the left rail's section list and the
// "Filters used n/9" count above it.
//
// ⚠️ IT IS NOT `CRITERIA_SECTIONS`, AND REUSING THAT LIST IS WRONG IN BOTH DIRECTIONS. The two
// lists are built in different files, they carry different data, and they diverge:
//
//   - the nav ALWAYS lists Keyword search; the section table renders it only when embedded;
//   - the nav's last two entries depend on live API answers (the ATS-filters configuration's
//     field count, and the external-source configuration's length), not on the section table's
//     `atsLabel !== undefined` / `hasExternalSources`;
//   - each nav entry carries the CRITERIA PROPERTY NAMES that make it "used", which the
//     section table has no notion of — and Work experience maps to SIX of them.
//
// The count in the header is the number of options whose properties appear in the criteria,
// over the number of options — so both halves move with the tenant's configuration. A
// hardcoded "/9" is right only for a tenant with ATS filters and no external sources.

export interface CriteriaNavigationOption {
  title: string
  /** The scroll anchor — the section's `id`, which for two of them is not its title. */
  elementId: string
  /**
   * The criteria keys that mark this section used. ⚠️ `isUsed` is "any key PRESENT in the
   * criteria object", not "any key with a truthy value" — upstream builds a `Set` of
   * `Object.keys(extensionCriteria)` and asks whether the option names one of them.
   */
  criteriaPropertyNames: readonly string[]
}

export interface CriteriaNavigationConfig {
  /** `getAtsLabel($window)` (the display label, 5cfd3ef899), and only used when the ATS-filters section is offered. */
  atsLabel?: string
  /**
   * `atsFiltersCriteriaEnabled && configuration?.fields?.length > 0` — the whole condition,
   * resolved by the caller because it takes an API round trip.
   */
  hasAtsFilters?: boolean
  /** `externalSourceConfigurationResponse.configurations.length > 0`. */
  hasExternalSources?: boolean
}

/** The seven that are always there, in template order. */
const ALWAYS: readonly CriteriaNavigationOption[] = [
  {
    title: 'Field of expertise',
    elementId: 'field-of-expertise-criteria-id',
    criteriaPropertyNames: ['fieldOfExpertise'],
  },
  { title: 'Skill set', elementId: 'skills-criteria-id', criteriaPropertyNames: ['skills'] },
  {
    title: 'Work experience',
    elementId: 'work-experience-criteria-id',
    // ⚠️ SIX KEYS, because "work experience" is six independent criteria in one section. Using
    // one key here — the obvious `['workExperience']`, which does not exist — leaves the entry
    // permanently unused and the counter permanently low.
    criteriaPropertyNames: [
      'industries',
      'companies',
      'companyTypes',
      'companyTiers',
      'qualityCompanyOnly',
      'candidateTurnover',
    ],
  },
  { title: 'Education', elementId: 'education-criteria-id', criteriaPropertyNames: ['education'] },
  { title: 'Location', elementId: 'location-criteria-id', criteriaPropertyNames: ['location'] },
  { title: 'Contact details', elementId: 'contact-details-criteria-id', criteriaPropertyNames: ['contactDetails'] },
  { title: 'Keyword search', elementId: 'keywords-criteria-id', criteriaPropertyNames: ['keywords'] },
]

export function criteriaNavigationOptions(config: CriteriaNavigationConfig = {}): CriteriaNavigationOption[] {
  const options = [...ALWAYS]

  if (config.hasAtsFilters === true) {
    options.push({
      title: `${config.atsLabel ?? ''} filters`,
      elementId: 'ats-filters-criteria-id',
      criteriaPropertyNames: ['atsFilters'],
    })
  }

  if (config.hasExternalSources === true) {
    options.push({
      title: 'External sources filters',
      elementId: 'external-source-filters-criteria-id',
      criteriaPropertyNames: ['externalSourceSearchCriteriaFilters'],
    })
  }

  return options
}

/** Which options the current criteria object marks used — presence of the KEY, not its value. */
export function usedNavigationOptions(
  options: readonly CriteriaNavigationOption[],
  criteria: Readonly<Record<string, unknown>>,
): Set<string> {
  const present = new Set(Object.keys(criteria))
  return new Set(
    options
      .filter((option) => option.criteriaPropertyNames.some((name) => present.has(name)))
      .map((option) => option.elementId),
  )
}
